import { useCallback, useMemo } from "react";
import {
  EntitySelector,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelectedChips,
  type EntityItemConfig,
  type EntitySelection,
  type TableFilter,
} from "@salestrak/ui";

/**
 * The entity-filter body for `DataTableFilterButton`'s `renderEntityFilter`
 * slot: an inline entity selector over the filter def's pools, round-tripping
 * the filter's id-array values through `EntitySelection`s.
 *
 * The library particle deliberately renders nothing for an entity filter it
 * is not handed a renderer for — entity rows and their avatars are app
 * vocabulary. Every `DataTableFilterButton` call site passes
 * `renderTableEntityFilter`.
 */
export function TableEntityFilter({
  filter,
  setValues,
}: {
  filter: TableFilter<EntityItemConfig>;
  setValues: (key: string, values: string[]) => void;
}) {
  const selectorConfig = filter.entitySelectorConfig;

  const entityById = useMemo(() => {
    const map = new Map<string, { poolId: string; entity: EntityItemConfig }>();
    for (const pool of selectorConfig?.pools ?? []) {
      for (const item of pool.items) {
        map.set(item.id, { entity: item, poolId: pool.id });
      }
    }
    return map;
  }, [selectorConfig]);

  const selections = useMemo<EntitySelection[]>(() => {
    const picked: EntitySelection[] = [];
    for (const id of filter.values) {
      const entry = entityById.get(id);
      if (entry) picked.push({ entity: entry.entity, id, poolId: entry.poolId });
    }
    return picked;
  }, [filter.values, entityById]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      setValues(
        filter.key,
        next.map((selection) => selection.id),
      );
    },
    [filter.key, setValues],
  );

  if (!selectorConfig) return null;

  return (
    <EntitySelector
      variant="inline"
      config={selectorConfig}
      value={selections}
      onValueChange={handleValueChange}
    >
      <EntitySelectorInput />
      <EntitySelectorSelectedChips label="Selected" />
      {selectorConfig.pools.map((pool) => (
        <EntitySelectorPool key={pool.id} value={pool.id} />
      ))}
    </EntitySelector>
  );
}

/** Stable render-prop form for `DataTableFilterButton renderEntityFilter=`. */
export function renderTableEntityFilter(args: {
  filter: TableFilter<EntityItemConfig>;
  setValues: (key: string, values: string[]) => void;
}) {
  return <TableEntityFilter {...args} />;
}
